import { Box, Card, keyframes, Link, styled, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useCampaign } from '@/api/campaigns/get'
import anotherPlanetImg from '@/assets/images/admin-profile/another_planet.svg'
import averageImage from '@/assets/images/score-widget/average.svg'
import badImage from '@/assets/images/score-widget/bad.svg'
import excellentImage from '@/assets/images/score-widget/excellent.svg'
import lowImage from '@/assets/images/score-widget/low.svg'
import widgetBackgroundImage from '@/assets/images/widget_background.svg'
import CardHeader from '@/common/components/Card/CardHeader'
import InfoTooltip from '@/common/components/InfoTooltip/InfoTooltip'
import { theme } from '@/theme/theme'
type CampaignScoreProps = {
  campaignId: string
}

const CampaignScore: FC<CampaignScoreProps> = ({ campaignId }) => {
  const [animationKey, setAnimationKey] = useState(0)
  const { t } = useTranslation()

  const { data: campaignData, isPending: isPendingCampaign } = useCampaign(campaignId)

  const restartAnimation = () => {
    setAnimationKey((prevKey) => prevKey + 1)
  }

  if (isPendingCampaign || !campaignData) return null // Add a loading spinner here or use no score component

  const change =
    campaignData.score > -1 && campaignData.previous_campaign_score > -1
      ? campaignData.score - campaignData.previous_campaign_score
      : undefined

  return (
    <Card>
      <CardHeader title={t('campaignDashboard.campaignScore.title')}>
        <InfoTooltip
          title={
            <Trans
              i18nKey={'campaignDashboard.campaignScore.info'}
              components={{
                1: (
                  <Link href="https://catalog.cywareness.io/user_manual" color={theme.palette.white} target="_blank" />
                ),
              }}
            />
          }
          fontSize={20}></InfoTooltip>
      </CardHeader>
      {campaignData.score >= 0 ? (
        <StyledContainerWithBackground key={animationKey}>
          <StyledBadge onClick={restartAnimation} score={campaignData.score}>
            <StyledPopInTypography fontSize={56} paddingTop={4.5}>
              {campaignData.score.toFixed()}
            </StyledPopInTypography>
            <StyledPopInTypography fontSize={26} paddingTop={5}>
              {t(
                'campaignDashboard.campaignScore.' +
                  (campaignData.score <= 25
                    ? 'bad'
                    : campaignData.score <= 50
                    ? 'low'
                    : campaignData.score <= 75
                    ? 'average'
                    : 'excellent')
              )}
            </StyledPopInTypography>
          </StyledBadge>
          {(!!change || change === 0) && (
            <Typography display="flex" alignItems="center" gap={1} textAlign="center">
              <StyledPopInTypography fontSize={20}>{Math.abs(change).toFixed() + '%'}</StyledPopInTypography>{' '}
              <Trans
                i18nKey={'campaignDashboard.campaignScore.comparison'}
                values={{
                  operator: t(
                    'campaignDashboard.campaignScore.' +
                      (change > 0 ? 'higherThan' : change < 0 ? 'lowerThan' : 'changeFrom')
                  ),
                }}
              />
            </Typography>
          )}
        </StyledContainerWithBackground>
      ) : (
        <StyledNoScoreContainer>
          <StyledNoScoreImage />
          <Typography fontSize={14}>{t('campaignDashboard.campaignScore.noInteractions')}</Typography>
        </StyledNoScoreContainer>
      )}
    </Card>
  )
}

const StyledContainerWithBackground = styled(Box)(() => ({
  backgroundImage: `url(${widgetBackgroundImage})`,
  backgroundSize: '126px',
  minWidth: '143px',
  width: '100%',
  minHeight: '256px',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: theme.spacing(3),
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}))

const StyledBadge = styled(Box)(({ score }: { score: number }) => ({
  backgroundImage: `url(${
    score <= 25 ? badImage : score <= 50 ? lowImage : score <= 75 ? averageImage : excellentImage
  })`,
  height: '156px',
  width: '156px',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  cursor: 'pointer',
  position: 'relative',
}))

const StyledNoScoreImage = styled(Box)(() => ({
  backgroundImage: `url(${anotherPlanetImg})`,
  backgroundSize: '264px',
  backgroundRepeat: 'no-repeat',
  width: '264px',
  height: '180px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}))
const StyledNoScoreContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  gap: theme.spacing(3),
}))

const popIn = keyframes`
  0% { opacity: 0; scale: 0.4 }
  75% { opacity: 1; scale: 1.25 }
  100% { opacity: 1; scale: 1}
`

const StyledPopInTypography = styled(Typography)(() => ({
  fontWeight: theme.typography.fontWeightBold,
  animation: `${popIn} 0.5s forwards`,
}))

export default CampaignScore
